/**
 * Product recommendations.
 *
 * The `recommendations` Liquid object is empty on the product page itself — it is only
 * populated on `/recommendations/products`. So the section ships empty and this element
 * fetches the rendered section back into itself.
 *
 * Deferred until the element is close to the viewport: recommendations sit below the
 * whole product page, and a request made at page load competes with the images the
 * shopper is actually looking at.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true' || !this.dataset.url) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '400px' }
    );

    observer.observe(this);
  }

  /* Returns the section's inner markup, or null when the API had nothing to recommend. */
  async fetchGrid(url) {
    const response = await fetch(url);
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.querySelector('product-recommendations');

    return fresh && fresh.querySelector('.product-grid') ? fresh.innerHTML : null;
  }

  async load() {
    this.dataset.loaded = 'true';

    try {
      /* Complementary first, related second: a product with no complementary list set
         still gets a filled block rather than a gap. The second request only happens on
         that miss, so the common path stays one request. */
      let markup = await this.fetchGrid(this.dataset.url);

      if (!markup && this.dataset.fallbackUrl) {
        markup = await this.fetchGrid(this.dataset.fallbackUrl);
      }

      /* Nothing to recommend is a normal outcome on a 15-product catalogue, and an empty
         heading over an empty grid is worse than no section at all. */
      if (!markup) return;

      this.innerHTML = markup;
    } catch {
      /* A failed recommendation request leaves the page exactly as it was. */
    }
  }
}

/* Guarded, like every other element in the theme: the theme editor can load this script
   twice when a section is re-added, and a second define() on a taken name throws. */
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
